// ---------------------------------------------------------------------------
// PB-T.31: Post-publish dashboard widget — mounted pages summary
// ---------------------------------------------------------------------------

import type { ComponentType } from "react";
import { usePageList, type PageSummary } from "@/hooks/use-pages";
import { usePageHealth, usePageRecommendations } from "@/hooks/use-self-learning";
import { Skeleton } from "@/components/skeleton";
import {
  Globe,
  FileCheck2,
  FileText,
  PencilRuler,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Rocket,
  Wand2,
} from "lucide-react";

interface SiteInfo {
  name: string;
  url: string;
  isPublished: boolean;
  pagesPublished: number;
  draftPages: number;
  totalPages: number;
  secureUrl: boolean;
  primaryPublishedPage: PageSummary | null;
}

interface HealthCheck {
  label: string;
  ok: boolean;
}

function StatBox({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="stagger-item flex min-w-0 flex-col items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-3">
      <Icon className="h-4 w-4 text-[var(--color-muted-foreground)]" />
      <span className="text-lg font-bold tabular-nums text-[var(--color-foreground)]">
        {value.toLocaleString()}
      </span>
      <span className="truncate text-xs text-[var(--color-muted-foreground)]">
        {label}
      </span>
    </div>
  );
}

function HealthCheckItem({ label, ok }: HealthCheck) {
  return (
    <div className="stagger-item flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-[var(--color-success)]" />
      ) : (
        <AlertTriangle className="h-4 w-4 shrink-0 text-[var(--color-warning)]" />
      )}
      <span className="text-[var(--color-foreground)]">{label}</span>
    </div>
  );
}

function UnpublishedCta() {
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <Rocket className="h-8 w-8 text-[var(--color-muted-foreground)]" />
      <p className="text-sm font-medium text-[var(--color-foreground)]">
        Publish your first page
      </p>
      <p className="max-w-[240px] text-xs text-[var(--color-muted-foreground)]">
        Build and publish a page to see real site status here.
      </p>
    </div>
  );
}

function WidgetSkeleton() {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
      <Skeleton className="mb-4 h-4 w-32" />
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-md" />
        ))}
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-3 w-40" />
      </div>
    </div>
  );
}

function buildSiteInfo(pages: PageSummary[]): SiteInfo | null {
  if (pages.length === 0) {
    return null;
  }

  const publishedPages = pages.filter((page) => page.status === "published");
  const draftPages = pages.filter((page) => page.status === "draft");
  const primaryPublishedPage = publishedPages[0] ?? null;

  return {
    name: primaryPublishedPage?.title ?? pages[0]?.title ?? "Pages",
    url: primaryPublishedPage?.publishedUrl ?? "",
    isPublished: publishedPages.length > 0,
    pagesPublished: publishedPages.length,
    draftPages: draftPages.length,
    totalPages: pages.length,
    secureUrl: Boolean(primaryPublishedPage?.publishedUrl?.startsWith("https://")),
    primaryPublishedPage,
  };
}

export function SiteHealthWidget() {
  const { pages, isLoading, error } = usePageList();

  // Hooks must be called before any early returns (Rules of Hooks)
  const siteData = !isLoading && !error ? buildSiteInfo(pages) : null;
  const primaryPageId = siteData?.primaryPublishedPage?.id;
  const pageHealth = usePageHealth(primaryPageId);
  const pageRecommendations = usePageRecommendations(primaryPageId, { surface: "dashboard" });
  const isPublished = siteData?.isPublished === true;

  if (isLoading) {
    return <WidgetSkeleton />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm text-[var(--color-error)]">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Failed to load page health. {error}</span>
        </div>
      </div>
    );
  }

  const healthChecks: HealthCheck[] = siteData
    ? [
        { label: "Published page available", ok: siteData.pagesPublished > 0 },
        { label: "Secure published URL", ok: siteData.secureUrl },
        { label: `${siteData.draftPages} draft page${siteData.draftPages === 1 ? "" : "s"} tracked`, ok: true },
        { label: `${siteData.totalPages} total page${siteData.totalPages === 1 ? "" : "s"} in editor`, ok: siteData.totalPages > 0 },
      ]
    : [];

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[var(--color-primary)]" />
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
            {siteData?.name ?? "Site Health"}
          </h3>
        </div>
        {isPublished && siteData?.url && (
          <div className="flex items-center gap-3">
            {siteData.primaryPublishedPage ? (
              <a
                href={`/pages/editor/${siteData.primaryPublishedPage.id}?mode=simple`}
                className="text-xs font-medium text-[var(--color-primary)] hover:underline"
              >
                Edit website
              </a>
            ) : null}
            <a
              href={siteData.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
            >
              View live
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>

      {!isPublished && <UnpublishedCta />}

      {isPublished && siteData && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatBox
              label="Total Pages"
              value={siteData.totalPages}
              icon={FileText}
            />
            <StatBox
              label="Published"
              value={siteData.pagesPublished}
              icon={FileCheck2}
            />
            <StatBox
              label="Drafts"
              value={siteData.draftPages}
              icon={PencilRuler}
            />
            <StatBox
              label="Live URLs"
              value={siteData.url ? 1 : 0}
              icon={Globe}
            />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
              Site Health
            </p>
            {healthChecks.map((check) => (
              <HealthCheckItem
                key={check.label}
                label={check.label}
                ok={check.ok}
              />
            ))}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 border-t border-[var(--color-border)] pt-4 sm:grid-cols-3">
            <StatBox
              label="Visitors"
              value={pageHealth.health?.telemetry.pageViews ?? 0}
              icon={Globe}
            />
            <StatBox
              label="Contacts"
              value={pageHealth.health?.telemetry.directConversions ?? 0}
              icon={Rocket}
            />
            <StatBox
              label="Health Score"
              value={pageHealth.health?.overallScore ?? 0}
              icon={FileCheck2}
            />
          </div>

          {pageRecommendations.recommendations[0] ? (
            <div className="mt-5 rounded-2xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-4">
              <div className="flex items-start gap-3">
                <Wand2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-primary)]" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-primary)]">
                    Suggested next change
                  </p>
                  <p className="mt-2 text-sm font-medium text-[var(--color-foreground)]">
                    {pageRecommendations.recommendations[0].title}
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                    {pageRecommendations.recommendations[0].message}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
